/**
 * What each role's Discovery Home shows (release 3.1, persona D6; SPEC.md 7.1,
 * the "Discovery Home" column, carried in the generated table as DASHBOARDS).
 *
 * Every widget reads ONE of the data calls of the page it links to (`api` is
 * one of that page's `apis` in the persona table), and is shown only when the
 * caller may open that page (`canOpen`). So a widget can never 403 for a role
 * that sees it: the page's permissions are the call's permissions, and the
 * table decides the page. `role-home.test.ts` checks both halves for every
 * widget. A number is always a count the API made, never a constant.
 */
import type { DiscoveryRole } from '@/generated/persona/discovery-access';
import {
  getComplianceCoverage,
  getCompliancePostureFederation,
  getDecisionSummary,
  getDiscoveryCoverage,
  getScanSummary,
  getSdf,
  getTenantHealth,
  listBreachIncidents,
  listBreakGlassSessions,
  listCompoundFindings,
  listDataSources,
  listDetectionPlaybooks,
  listDetectionRules,
  listDiscoveryFindings,
  listDpias,
  listEdgeFleet,
  listErasureJobs,
  listEvidencePacks,
  listLicenses,
  listSonarFindings,
  listUebaFindings,
  type BreakGlassSession,
  type DetectionRuleStatus,
} from '@/lib/api';
import { listIdpConnections } from '@/lib/auth-admin-api';

export interface WidgetValue {
  /** The headline number or word. */
  readonly value: string;
  /** One line under it. */
  readonly detail?: string;
}

export interface HomeWidget {
  readonly id: string;
  readonly title: string;
  /** The specification page it opens; the widget shows only when the caller may open it. */
  readonly pageId: string;
  /** The data call it makes: one of the page's `apis` in the persona table. */
  readonly api: string;
  readonly load: () => Promise<WidgetValue>;
}

const n = (v: number): string => v.toLocaleString('en-IN');

/** Up to `limit` open findings, with "+" when there are more. */
async function openFindings(): Promise<WidgetValue> {
  const r = await listDiscoveryFindings({ status: 'open', limit: 100 });
  return { value: `${n(r.findings.length)}${r.nextCursor ? '+' : ''}`, detail: 'open findings' };
}

function breakGlassPending(sessions: readonly BreakGlassSession[]): number {
  const now = Date.now();
  return sessions.filter((s) => !s.approvedAt && !s.revokedAt && new Date(s.expiresAt).getTime() > now).length;
}

function breakGlassActive(sessions: readonly BreakGlassSession[]): number {
  const now = Date.now();
  return sessions.filter((s) => s.approvedAt && !s.revokedAt && new Date(s.expiresAt).getTime() > now).length;
}

const W = {
  personalData: {
    id: 'home.personal_data',
    title: 'Assets holding personal data',
    pageId: 'disc.dspm_dashboard',
    api: 'GET /v1/discovery/coverage',
    load: async () => {
      const c = await getDiscoveryCoverage();
      return { value: n(c.classifiedAssets), detail: `${n(c.scannedAssets)} of ${n(c.totalAssets)} assets scanned` };
    },
  },
  findingsOpen: {
    id: 'home.findings_open',
    title: 'Findings awaiting triage',
    pageId: 'disc.dspm_findings',
    api: 'GET /v1/discovery/findings',
    load: openFindings,
  },
  erasureAwaitingApproval: {
    id: 'home.erasure_pending',
    title: 'Erasure jobs awaiting approval',
    pageId: 'disc.comp_erasure',
    api: 'GET /v1/compliance/erasure-jobs',
    load: async () => {
      const r = await listErasureJobs({ limit: 1 });
      return { value: n(r.byStatus.pending), detail: `${n(r.byStatus.approved)} approved, not yet run` };
    },
  },
  erasureToExecute: {
    id: 'home.erasure_to_execute',
    title: 'Erasure jobs to execute',
    pageId: 'disc.comp_erasure',
    api: 'GET /v1/compliance/erasure-jobs',
    load: async () => {
      const r = await listErasureJobs({ limit: 1 });
      return { value: n(r.byStatus.approved), detail: `${n(r.byStatus.running)} running` };
    },
  },
  breachesOpen: {
    id: 'home.breaches_open',
    title: 'Breaches not yet notified',
    pageId: 'disc.comp_breaches',
    api: 'GET /v1/compliance/breach-incidents',
    load: async () => {
      const r = await listBreachIncidents({ limit: 1 });
      return { value: n(r.byStatus.draft + r.byStatus.acknowledged), detail: `${n(r.byStatus.acknowledged)} acknowledged` };
    },
  },
  breachNoticesToApprove: {
    id: 'home.breach_notices',
    title: 'Breach notifications awaiting approval',
    pageId: 'disc.comp_breaches',
    api: 'GET /v1/compliance/breach-incidents',
    load: async () => {
      const r = await listBreachIncidents({ limit: 1 });
      return { value: n(r.byStatus.acknowledged), detail: 'acknowledged, not yet notified' };
    },
  },
  rulesAwaitingApproval: {
    id: 'home.rules_pending',
    title: 'Rules awaiting publish',
    pageId: 'disc.ddr_rules',
    api: 'GET /v1/detection/rules',
    load: async () => {
      // A rule waits to be published while it is draft or dry_run (server
      // statuses, DETECTION_RULE_STATUSES); typed so a status the server never
      // sets fails the typecheck.
      const r = await listDetectionRules();
      const count = (status: DetectionRuleStatus) => r.rules.filter((x) => x.status === status).length;
      const draft = count('draft');
      const dryRun = count('dry_run');
      return { value: n(draft + dryRun), detail: `${n(draft)} draft, ${n(dryRun)} dry run, ${n(r.rules.length)} rules in all` };
    },
  },
  breakGlassRequests: {
    id: 'home.break_glass_requests',
    title: 'Break-glass requests awaiting approval',
    pageId: 'disc.ddr_break_glass',
    api: 'GET /v1/detection/break-glass',
    load: async () => {
      const r = await listBreakGlassSessions();
      return { value: n(breakGlassPending(r.sessions)), detail: `${n(breakGlassActive(r.sessions))} active` };
    },
  },
  breakGlassState: {
    id: 'home.break_glass_state',
    title: 'Break-glass sessions active',
    pageId: 'disc.ddr_break_glass',
    api: 'GET /v1/detection/break-glass',
    load: async () => {
      const r = await listBreakGlassSessions();
      return { value: n(breakGlassActive(r.sessions)), detail: `${n(breakGlassPending(r.sessions))} awaiting approval` };
    },
  },
  scans: {
    id: 'home.scans',
    title: 'Scans completed',
    pageId: 'disc.dspm_scans',
    api: 'GET /v1/discovery/scans/summary',
    load: async () => {
      const s = await getScanSummary();
      return { value: `${n(s.completed)} / ${n(s.total)}`, detail: `${n(s.failed)} failed, ${n(s.running)} running` };
    },
  },
  dataSources: {
    id: 'home.data_sources',
    title: 'Data sources',
    pageId: 'disc.adm_data_sources',
    api: 'GET /v1/data-sources',
    load: async () => {
      const r = await listDataSources();
      return { value: n(r.count), detail: 'registered' };
    },
  },
  edgePods: {
    id: 'home.edge_pods',
    title: 'Edge pods healthy',
    pageId: 'disc.adm_edge_fleet',
    api: 'GET /v1/edge/fleet',
    load: async () => {
      const r = await listEdgeFleet({ limit: 1 });
      return { value: `${n(r.summary.healthy)} / ${n(r.summary.total)}`, detail: `${n(r.summary.down + r.summary.silent)} down or silent` };
    },
  },
  licences: {
    id: 'home.licences',
    title: 'Licences',
    pageId: 'disc.adm_licences',
    api: 'GET /v1/foundational/licenses',
    load: async () => {
      const r = await listLicenses();
      const active = r.licenses.filter((l) => l.status === 'active' || l.status === 'trial');
      return { value: n(active.length), detail: `of ${n(r.licenses.length)} active or in trial` };
    },
  },
  identityProviders: {
    id: 'home.idp',
    title: 'SSO connections',
    pageId: 'disc.adm_idp',
    api: 'GET /v1/auth/sso/idp-connections',
    load: async () => {
      const r = await listIdpConnections();
      return { value: n(r.length), detail: 'configured' };
    },
  },
  tenantHealth: {
    id: 'home.tenant_health',
    title: 'Tenant health',
    pageId: 'disc.adm_health',
    api: 'GET /v1/admin/health',
    load: async () => {
      const h = await getTenantHealth();
      return { value: h.overall.status, detail: h.overall.weakSpots.length ? `${n(h.overall.weakSpots.length)} weak spots` : 'no weak spots' };
    },
  },
  dpias: {
    id: 'home.dpias',
    title: 'DPIAs in progress',
    pageId: 'disc.comp_dpia',
    api: 'GET /v1/compliance/dpia',
    load: async () => {
      const r = await listDpias({ limit: 1 });
      return { value: n((r.byStatus.draft ?? 0) + (r.byStatus.reviewed ?? 0)), detail: `${n(r.byStatus.approved ?? 0)} approved` };
    },
  },
  sdf: {
    id: 'home.sdf',
    title: 'SDF obligations',
    pageId: 'disc.comp_sdf',
    api: 'GET /v1/compliance/sdf-obligations',
    load: async () => {
      const r = await getSdf();
      return { value: r.sdf ? 'Recorded' : 'Not recorded', detail: 'DPDPA section 10' };
    },
  },
  evidencePacks: {
    id: 'home.evidence_packs',
    title: 'Evidence packs',
    pageId: 'disc.comp_coverage',
    api: 'GET /v1/compliance/evidence-packs',
    load: async () => {
      const r = await listEvidencePacks({ limit: 1 });
      return { value: n(r.count), detail: 'built' };
    },
  },
  regulatoryCoverage: {
    id: 'home.regulatory_coverage',
    title: 'Regulatory coverage',
    pageId: 'disc.comp_center',
    api: 'GET /v1/compliance/coverage',
    load: async () => {
      const c = await getComplianceCoverage();
      return { value: `${Math.round(c.overall.score * 100)}%`, detail: `${n(c.overall.greenRegulations)} regulations green` };
    },
  },
  compliancePosture: {
    id: 'home.compliance_posture',
    title: 'Open data subject requests',
    pageId: 'disc.adm_posture',
    api: 'GET /v1/foundational/compliance-posture-federation',
    load: async () => {
      const p = await getCompliancePostureFederation();
      return { value: n(p.dsars.open), detail: `${n(p.dsars.pastSla)} past SLA` };
    },
  },
  decisions: {
    id: 'home.decisions',
    title: 'DDR decisions in the last hour',
    pageId: 'disc.ddr_dashboard',
    api: 'GET /v1/detection/decisions/summary',
    load: async () => {
      const s = await getDecisionSummary();
      return { value: n(s.lastHour), detail: `${n(s.blocked24h)} blocked in 24 hours` };
    },
  },
  ueba: {
    id: 'home.ueba',
    title: 'UEBA alerts open',
    pageId: 'disc.dam_ueba',
    api: 'GET /v1/activity/ueba/findings',
    load: async () => {
      const r = await listUebaFindings();
      return { value: n(r.findings.filter((f) => f.status === 'open').length), detail: `${n(r.findings.length)} in all` };
    },
  },
  sonar: {
    id: 'home.sonar',
    title: 'Sonar alerts open',
    pageId: 'disc.dam_sonar',
    api: 'GET /v1/activity/sonar/findings',
    load: async () => {
      const r = await listSonarFindings({ status: 'open', limit: 100 });
      return { value: n(r.findings.length), detail: 'to triage' };
    },
  },
  killChain: {
    id: 'home.kill_chain',
    title: 'Kill-chain findings open',
    pageId: 'disc.dam_kill_chain',
    api: 'GET /v1/activity/compound-findings',
    load: async () => {
      const r = await listCompoundFindings({ status: 'open', limit: 100 });
      return { value: n(r.count), detail: 'correlated across engines' };
    },
  },
  playbooks: {
    id: 'home.playbooks',
    title: 'Playbooks',
    pageId: 'disc.ddr_playbooks',
    api: 'GET /v1/detection/playbooks',
    load: async () => {
      const r = await listDetectionPlaybooks();
      return { value: n(r.playbooks.length), detail: 'ready to run' };
    },
  },
} satisfies Record<string, HomeWidget>;

/**
 * Each built-in role's Home, in the order of SPEC.md 7.1. A custom role gets
 * every widget whose page it may open (its grants decide), de-duplicated.
 */
export const ROLE_HOME: Readonly<Record<DiscoveryRole, readonly HomeWidget[]>> = {
  dpo: [W.personalData, W.findingsOpen, W.erasureAwaitingApproval, W.breachesOpen, W.rulesAwaitingApproval, W.breakGlassRequests],
  it_admin: [W.dataSources, W.scans, W.edgePods, W.licences, W.identityProviders, W.tenantHealth],
  compliance_analyst: [W.findingsOpen, W.erasureToExecute, W.dpias, W.sdf, W.evidencePacks],
  legal_reviewer: [W.rulesAwaitingApproval, W.breachNoticesToApprove, W.breakGlassRequests, W.findingsOpen],
  security_operator: [W.decisions, W.ueba, W.sonar, W.killChain, W.playbooks, W.breakGlassState],
  read_only_security_operations: [W.regulatoryCoverage, W.evidencePacks, W.compliancePosture, W.findingsOpen],
};

/** Every widget, for a custom role and for the tests. */
export const ALL_WIDGETS: readonly HomeWidget[] = Object.values(W);
